import { AdvancedRealTimeChart } from "react-ts-tradingview-widgets";
import { useEffect, useRef, useMemo } from "react";

const STORAGE_KEY_PREFIX = "tvAdvancedChart_lastSymbol_";
const TRADINGVIEW_ORIGIN = "https://s.tradingview.com";

// eslint-disable-next-line react/prop-types
const TvAdvancedRealTimeChart = ({ widgetCompositeId }) => {
  const storageKey = `${STORAGE_KEY_PREFIX}${widgetCompositeId}`;
  const containerRef = useRef(null);
  const iframeRef = useRef(null);

  // Load saved symbol from localStorage - use useMemo so it never changes
  const symbol = useMemo(() => {
    const saved = localStorage.getItem(storageKey);
    return saved || "NASDAQ:AAPL";
  }, [storageKey]);

  const lastSymbolRef = useRef(symbol);

  // Locate the TradingView iframe once it mounts
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new MutationObserver(() => {
      const iframe = container.querySelector("iframe");
      if (iframe) {
        iframeRef.current = iframe;
        observer.disconnect();
      }
    });

    // Check if iframe already exists
    const existing = container.querySelector("iframe");
    if (existing) {
      iframeRef.current = existing;
    } else {
      observer.observe(container, { childList: true, subtree: true });
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleMessage = (event) => {
      // Only process messages from TradingView
      if (event.origin !== TRADINGVIEW_ORIGIN) return;

      // Walk up the frame hierarchy from event.source to check if
      // our iframe is an ancestor. This handles nested iframes since
      // event.source may be a deeply nested child, not the top-level
      // iframe we found in the DOM.
      const target = iframeRef.current?.contentWindow;
      if (!target) return;

      let isOurs = false;
      let source = event.source;
      try {
        while (source && source !== window) {
          if (source === target) {
            isOurs = true;
            break;
          }
          const parent = source.parent;
          if (parent === source) break;
          source = parent;
        }
      } catch {
        // Cross-origin access may throw in some browsers
        return;
      }
      if (!isOurs) return;

      try {
        const data =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data;

        // Check if this is a quoteUpdate message with symbol info
        if (data?.name === "quoteUpdate" && data?.data?.original_name) {
          const newSymbol = data.data.original_name;

          // If the symbol changed, save it to localStorage
          if (newSymbol !== lastSymbolRef.current) {
            lastSymbolRef.current = newSymbol;
            localStorage.setItem(storageKey, newSymbol);
          }
        }
      } catch (error) {
        // Silently ignore parsing errors
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [storageKey]);

  return (
    <div ref={containerRef} style={{ position: "relative", overflow: "hidden", height: "100%" }}>
      <AdvancedRealTimeChart
        theme="dark"
        autosize
        isTransparent={true}
        symbol={symbol}
      />
    </div>
  );
};

export default TvAdvancedRealTimeChart;
